/**
 * Settings Applier - Applies user settings to the DOM
 *
 * Handles: color themes, font sizes, contrast modes, reduced motion
 * Uses CSS custom properties for real-time updates with sophisticated theming.
 */

import { useEffect } from 'react'
import { useSettingsStore, COLOR_THEMES } from '../stores/settingsStore'

export function SettingsApplier() {
  const { colorTheme, fontSize, highContrast, reducedMotion, showIconLabels } = useSettingsStore()

  // Apply color theme - comprehensive CSS variable injection
  useEffect(() => {
    const root = document.documentElement
    const theme = COLOR_THEMES[colorTheme]

    // Background layers
    root.style.setProperty('--color-bg', theme.bg)
    root.style.setProperty('--color-bg-secondary', theme.bgSecondary)
    root.style.setProperty('--color-bg-tertiary', theme.bgTertiary)

    // Accent colors
    root.style.setProperty('--color-accent', theme.accent)
    root.style.setProperty('--color-accent-muted', theme.accentMuted)
    root.style.setProperty('--color-highlight', theme.highlight)

    // Text colors (will be overridden by contrast mode if needed)
    root.style.setProperty('--color-text-primary', theme.textPrimary)
    root.style.setProperty('--color-text-secondary', theme.textSecondary)
    root.style.setProperty('--color-text-muted', theme.textMuted)

    // Borders
    root.style.setProperty('--color-border', theme.border)
    root.style.setProperty('--color-border-subtle', theme.borderSubtle)

    // Status colors
    root.style.setProperty('--color-success', theme.success)
    root.style.setProperty('--color-warning', theme.warning)
    root.style.setProperty('--color-error', theme.error)

    // Update body background for full coverage
    document.body.style.backgroundColor = theme.bg

    // Store theme name for component styling
    root.dataset.theme = colorTheme
  }, [colorTheme])

  // Apply font size
  useEffect(() => {
    const root = document.documentElement
    const sizes = {
      normal: '16px',
      large: '18px',
      xl: '20px',
    }
    root.style.setProperty('--font-size-base', sizes[fontSize])
    root.style.fontSize = sizes[fontSize]
  }, [fontSize])

  // Apply contrast mode - enhances text visibility
  useEffect(() => {
    const root = document.documentElement
    const theme = COLOR_THEMES[useSettingsStore.getState().colorTheme]

    if (highContrast === 'normal') {
      // Use theme defaults
      root.style.setProperty('--color-text-primary', theme.textPrimary)
      root.style.setProperty('--color-text-secondary', theme.textSecondary)
      root.style.setProperty('--color-border', theme.border)
    } else if (highContrast === 'high') {
      // Boost visibility
      root.style.setProperty('--color-text-primary', '#ffffff')
      root.style.setProperty('--color-text-secondary', 'rgba(255, 255, 255, 0.75)')
      root.style.setProperty('--color-border', 'rgba(255, 255, 255, 0.2)')
    } else if (highContrast === 'highest') {
      // Maximum visibility
      root.style.setProperty('--color-text-primary', '#ffffff')
      root.style.setProperty('--color-text-secondary', 'rgba(255, 255, 255, 0.9)')
      root.style.setProperty('--color-border', 'rgba(255, 255, 255, 0.3)')
    }
    root.dataset.contrast = highContrast
  }, [highContrast, colorTheme])

  // Apply reduced motion
  useEffect(() => {
    const root = document.documentElement
    if (reducedMotion) {
      root.classList.add('reduce-motion')
      root.style.setProperty('--transition-duration', '0ms')
    } else {
      root.classList.remove('reduce-motion')
      root.style.setProperty('--transition-duration', '200ms')
    }
  }, [reducedMotion])

  // Apply icon labels setting (via data attribute for CSS targeting)
  useEffect(() => {
    document.documentElement.dataset.iconLabels = showIconLabels ? 'true' : 'false'
  }, [showIconLabels])

  return null
}
